import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

const LoginPage = () => {
  const { user, loading, login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#666] animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    const result = await login(email, password);
    
    if (!result.success) {
      setError(result.error);
    }
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-[#E8E8E3] py-3 px-8">
        <img 
          src="/nova-logo.png"
          alt="Nova Homes"
          className="object-contain"
          style={{ height: '100px', width: 'auto' }}
        />
      </header>

      {/* Login Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white rounded-2xl border border-[#E8E8E3] p-8 shadow-sm">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-[#2D2D2D] mb-2">
              Inventory Portal
            </h1>
            <p className="text-[#666] text-base">
              Sign in to access project inventory
            </p>
          </div>

          {error && (
            <div 
              className="mb-6 p-4 rounded-xl bg-red-50 border border-red-100 text-red-700 text-base"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-base font-medium text-[#2D2D2D] mb-2">
                Email
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                data-testid="login-email"
                className="w-full h-12 text-base bg-[#FAFAF8] border-[#E8E8E3] text-[#2D2D2D] placeholder:text-[#999]"
              />
            </div>

            <div>
              <label className="block text-base font-medium text-[#2D2D2D] mb-2">
                Password
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  data-testid="login-password"
                  className="w-full h-12 text-base bg-[#FAFAF8] border-[#E8E8E3] text-[#2D2D2D] placeholder:text-[#999] pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#999] hover:text-[#666]"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              data-testid="login-submit"
              className="w-full h-12 text-base bg-[#2D2D2D] hover:bg-[#404040] text-white font-medium rounded-xl"
            >
              {isSubmitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <p className="text-center text-[#999] text-sm mt-8">
            Access restricted to team members
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
